import { useTranslation } from "react-i18next";
import React from "react";
import { Accordion } from "./ui/Accordion";
import useWindowWidthThreshold from "./hooks/useWindowWidthThreshold";
import { useLatchedTrue } from "./hooks/useLatchedTrue";
import type { CustomerPreOrder, PreOrderItem } from "../../common/jsonRpcTypes";
import ServiceChargeSelector from "./preOrders/ServiceChargeSelector";
import { formatCurrency } from "../../utils/countryLocaleMap";
import "../styles/order-summary.scss";
import MenuItemCard from "./shared/MenuItemCard";
import { usePreorderContext } from "./context/PreorderContext";

interface OrderSummaryProps {
  thumbnail?: string;
  venueTitle?: string;
  preorder?: CustomerPreOrder;
  shouldHidePrices?: boolean;
  canChangeServiceCharge?: boolean;
  onServiceChargeChange?: (percentage: number) => Promise<boolean>;
  requiredPaymentAmount?: number;
  /**
   * When false, the discretionary service charge row is still displayed (label,
   * percentage and amount) but the "Change" button is suppressed, so the guest
   * cannot adjust the charge from this summary. Defaults to true to preserve the
   * existing behaviour for callers such as the /book preorder flow.
   */
  allowServiceChargeChange?: boolean;
}

interface OrderTotalsProps {
  isTaxExclusive: boolean;
  preorder: CustomerPreOrder;
  subtotal: number;
  total: number;
  serviceChargeAmount: number;
  canChangeServiceCharge: boolean;
  onServiceChargeChange?: (percentage: number) => Promise<boolean>;
  effectiveRequiredPayment: number;
  showRequiredPayment: boolean;
  taxLabel: string;
  currency: string;
  countryCode: string;
}

const OrderTotals: React.FC<Readonly<OrderTotalsProps>> = ({
  isTaxExclusive,
  preorder,
  subtotal,
  total,
  serviceChargeAmount,
  canChangeServiceCharge,
  onServiceChargeChange,
  effectiveRequiredPayment,
  showRequiredPayment,
  taxLabel,
  currency,
  countryCode,
}) => {
  const { t } = useTranslation();

  // Keep the row visible while the charge is changeable so choosing "None" (which resets
  // the amount to 0) does not remove the selector — the customer must still be able to pick
  // another value. `canChangeServiceCharge` latches on in the parent once the selector has
  // been shown, so it stays available even if the recalculated required payment drops to 0.
  const serviceChargeRow = (serviceChargeAmount > 0 || canChangeServiceCharge) && (
    <div className="total-line">
      <ServiceChargeSelector
        currentPercentage={preorder.serviceChargePercentage ?? 0}
        currentAmount={serviceChargeAmount}
        onPercentageChange={onServiceChargeChange ?? (async () => true)}
        canChange={canChangeServiceCharge}
        currency={currency}
        countryCode={countryCode}
      />
    </div>
  );

  if (isTaxExclusive) {
    return (
      <div className="order-totals">
        <div className="total-line">
          <span>{t("net_label", "Net")}</span>
          <span>{formatCurrency(preorder.total_net ?? subtotal, currency, countryCode)}</span>
        </div>
        {serviceChargeRow}
        {preorder.tax_breakdown!.map((entry) => (
          <div key={entry.label} className="total-line tax-breakdown-row">
            <span>{entry.label}</span>
            <span>{formatCurrency(entry.amount, currency, countryCode)}</span>
          </div>
        ))}
        <div className="total-line total-final">
          <span>{t("tax_total_label", "Total")}</span>
          <span>{formatCurrency(preorder.total_gross ?? total, currency, countryCode)}</span>
        </div>
        {showRequiredPayment && (
          <>
            {preorder.requiredPaymentTaxBreakdown?.map((entry) => (
              <div key={`req-${entry.label}`} className="total-line tax-breakdown-row">
                <span>{entry.label}</span>
                <span>{formatCurrency(entry.amount, currency, countryCode)}</span>
              </div>
            ))}
            <div className="total-line total-final">
              <span>{t("amount_to_pay", "Amount to Pay")}</span>
              <span>{formatCurrency(effectiveRequiredPayment, currency, countryCode)}</span>
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="order-totals">
      <div className="total-line">
        <span>{t("subtotal", "Subtotal")}</span>
        <span>{formatCurrency(subtotal, currency, countryCode)}</span>
      </div>
      {serviceChargeRow}
      <div className="total-line total-final">
        <span>
          {t("total_label", "Total")} ({t("inc_vat", "inc {{taxLabel}}", { taxLabel })})
        </span>
        <span>{formatCurrency(total, currency, countryCode)}</span>
      </div>
      {showRequiredPayment && (
        <div className="total-line total-final">
          <span>
            {t("amount_to_pay", "Amount to Pay")} ({t("inc_vat", "inc {{taxLabel}}", { taxLabel })})
          </span>
          <span>{formatCurrency(effectiveRequiredPayment, currency, countryCode)}</span>
        </div>
      )}
    </div>
  );
};

function buildOrderItemKey(item: PreOrderItem): string {
  const base = `${item.id}-${item.menu_id}`;
  const hasForNameIndex = item.for_name_index !== undefined && item.for_name_index !== null;
  return hasForNameIndex ? `${base}-${item.for_name_index}` : base;
}

interface OrderSummaryItemProps {
  item: PreOrderItem;
  preorder: CustomerPreOrder;
}

const OrderSummaryItem: React.FC<Readonly<OrderSummaryItemProps>> = ({ item, preorder }) => {
  const { t } = useTranslation();
  const { currency, countryCode, hidePricesByMenu, forNames } = usePreorderContext();

  const shouldHideItemPrice = hidePricesByMenu?.[item.menu_id] ?? false;
  const isMultiplePeople = forNames && forNames.length > 1;
  const forNameIndex = item.for_name_index ?? 0;
  const forName = isMultiplePeople && forNames[forNameIndex] ? forNames[forNameIndex] : undefined;

  // Fixed-price menu container: show header + nested items
  if (item.type === "menu" && Array.isArray(item.items) && item.items.length > 0) {
    const effectiveMenuId = item.items[0]?.menu_id ?? item.menu_id;
    const menu = preorder?.menus?.find((m) => m.id === effectiveMenuId);
    const fixedPrice = item.total ?? menu?.fixed_price_rules?.[0]?.price;

    return (
      <li className="order-summary-item p-bs-3 gcw:w-full">
        <div className="fw-bold gcw:w-full">
          <div className="fixed-price-menu-header gcw:flex gcw:justify-between gcw:items-center">
            <span className="fixed-price-menu-title gcw:font-semibold">{menu?.name ?? item.name}</span>
            {!shouldHideItemPrice && fixedPrice !== undefined && (
              <span className="gcw:ms-1 fixed-price-menu-price">
                {formatCurrency(fixedPrice, currency, countryCode)}
              </span>
            )}
          </div>
          {forName && (
            <span className="info-text for-name-text">{t("for_name", "For {{name}}", { name: forName })}</span>
          )}
          <div className="fixed-price-menu-items">
            <ul className="basket-list menu-list fixed-price-items-list">
              {item.items.map((nestedItem, nestedIdx: number) => (
                <li key={`${nestedItem.id}-${nestedIdx}`} style={{ marginBottom: "0.5rem" }}>
                  <MenuItemCard
                    variant="summary"
                    name={nestedItem.name}
                    price={nestedItem.price}
                    allergens={nestedItem.allergens}
                    options={nestedItem.options}
                    hidePrices={true}
                    currency={currency}
                    countryCode={countryCode}
                    actions={
                      <div className="gcw:flex gcw:items-center menu-quantity-controls">
                        <span className="info-text quantity-text">{nestedItem.quantity}</span>
                      </div>
                    }
                  />
                </li>
              ))}
            </ul>
          </div>
        </div>
      </li>
    );
  }

  return (
    <li className="order-summary-item gcw:flex gcw:justify-between gcw:items-start gcw:p-bs-3">
      <MenuItemCard
        variant="summary"
        name={item.name}
        price={item.price}
        allergens={item.allergens}
        options={item.options}
        hidePrices={shouldHideItemPrice}
        currency={currency}
        countryCode={countryCode}
        forName={forName}
        actions={
          <div className="gcw:flex gcw:items-center menu-quantity-controls">
            <span className="info-text quantity-text">{item.quantity}</span>
          </div>
        }
      />
    </li>
  );
};

const OrderSummary: React.FC<OrderSummaryProps> = ({
  preorder,
  shouldHidePrices,
  canChangeServiceCharge = false,
  onServiceChargeChange,
  requiredPaymentAmount = 0,
  allowServiceChargeChange = true,
}) => {
  const { currency, countryCode, hidePricesByMenu } = usePreorderContext();
  const { t } = useTranslation();
  const isAccordionDisabled = useWindowWidthThreshold(1025);
  // Latch the caller's permission, not the monetary amount: a configured charge can
  // compute to 0 (a percentage of a tiny order rounds down) or be briefly absent from
  // a recalculation, and neither means the guest may no longer set the rate. Picking
  // "None" also zeroes the amount, and the selector must stay so another value can be
  // chosen. Callers must only pass `canChangeServiceCharge` when a rate is configured —
  // otherwise a booking with no service charge would show a "0%" row.
  const canChangeServiceChargeEver = useLatchedTrue(canChangeServiceCharge);

  if (!preorder?.items || preorder.items.length === 0) {
    return null;
  }

  const isTaxExclusive =
    preorder.tax_inclusive === false && Array.isArray(preorder.tax_breakdown) && preorder.tax_breakdown.length > 0;
  const taxLabel = preorder.tax_label ?? "VAT";

  const orderItems = preorder.items.filter((item) => item.quantity > 0);

  if (orderItems.length === 0) {
    return null;
  }

  const totalItemsCount = orderItems.reduce((total, item) => total + Math.max(item.quantity, 0), 0);
  const subtotal = preorder.totalCost ?? 0;
  const serviceChargeAmount = preorder.serviceChargeAmount ?? 0;
  const total = subtotal + serviceChargeAmount;

  const canChangeServiceChargeNow = canChangeServiceChargeEver && allowServiceChargeChange;

  // Required payment display: use gross values for tax-exclusive venues
  const displayedTotal = isTaxExclusive ? (preorder.total_gross ?? total) : total;
  const effectiveRequiredPayment = isTaxExclusive
    ? (preorder.requiredPaymentGross ?? requiredPaymentAmount)
    : requiredPaymentAmount;
  const showRequiredPayment = effectiveRequiredPayment > 0 && effectiveRequiredPayment !== displayedTotal;

  const hasAnyHiddenPrice = orderItems.some((item) => hidePricesByMenu?.[item.menu_id]);
  const itemCountKey = totalItemsCount === 1 ? "item" : "items";
  const itemCountLabel = t(itemCountKey, itemCountKey);

  const orderContent = (
    <>
      <div className="gcw:p-bs-3 order-summary-header">
        <h2 className="gcw:font-bold gcw:text-xl gcw:mb-0">{t("order_summary", "Order Summary")}</h2>
      </div>

      <ul id="gc-order-summary-list" className="basket-list menu-list order-summary-list">
        {orderItems.map((item) => (
          <OrderSummaryItem key={buildOrderItemKey(item)} item={item} preorder={preorder} />
        ))}
      </ul>

      {!hasAnyHiddenPrice && (
        <OrderTotals
          isTaxExclusive={isTaxExclusive}
          preorder={preorder}
          subtotal={subtotal}
          total={total}
          serviceChargeAmount={serviceChargeAmount}
          canChangeServiceCharge={canChangeServiceChargeNow}
          onServiceChargeChange={onServiceChargeChange}
          effectiveRequiredPayment={effectiveRequiredPayment}
          showRequiredPayment={showRequiredPayment}
          taxLabel={taxLabel}
          currency={currency}
          countryCode={countryCode}
        />
      )}
    </>
  );

  return (
    <div id="gc-order-summary" className="order-summary">
      {isAccordionDisabled ? (
        orderContent
      ) : (
        <Accordion defaultActiveKey={undefined}>
          <Accordion.Item eventKey="0">
            {totalItemsCount > 0 && (
              <Accordion.Header>
                <span className="gcw:ms-bs-3">
                  {totalItemsCount} {itemCountLabel}
                  {!hasAnyHiddenPrice && (
                    <>
                      {" - "}
                      {formatCurrency(displayedTotal, currency, countryCode)}
                    </>
                  )}
                </span>
              </Accordion.Header>
            )}
            <Accordion.Body>{orderContent}</Accordion.Body>
          </Accordion.Item>
        </Accordion>
      )}
    </div>
  );
};

export default OrderSummary;
